import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Fuse from "fuse.js";
import React from "react";
import { useApiQuery } from "../hooks";
import type { Test } from "../types";

type Props = {
  title?: string;
  value: string[];
  single?: boolean;
  onChange: (ids: string[]) => void;
};

export const TestSplitSelect = ({ title, value, single, onChange }: Props) => {
  const { data: tests, isLoading: isLoadingTests } = useApiQuery<Test[]>("/tests");

  const [searchQuery, setSearchQuery] = React.useState("");

  const fuse = React.useMemo(() => {
    return new Fuse(tests ?? [], {
      keys: ["name", "id"],
      threshold: 0.3,
    });
  }, [tests]);

  return (
    <Grid container spacing={{ xs: 2 }}>
      <Grid size={{ xs: 12 }}>
        <Stack direction="column" spacing={1} alignItems="flex-start">
          <Typography variant="subtitle1">{title ?? "Select Tests"}</Typography>
          <TextField
            autoFocus
            size="small"
            label="Search Tests"
            placeholder="Search by name..."
            variant="outlined"
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </Stack>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Stack direction="column" spacing={2} alignItems="stretch" justifyContent="space-between">
          <Typography variant="subtitle2">All Tests ({tests?.length ?? 0})</Typography>
          {isLoadingTests && <CircularProgress />}
          {tests && (
            <FormGroup sx={{ height: "300px", overflowY: "auto" }}>
              <Stack direction="column">
                {searchQuery &&
                  fuse
                    .search(searchQuery, {
                      limit: 6,
                    })
                    .map(({ item }) => (
                      <FormControlLabel
                        key={item.id}
                        control={
                          <Checkbox
                            size="small"
                            checked={value.includes(item.id)}
                            onChange={() => {
                              let newValue = value.includes(item.id)
                                ? value.filter((id) => id !== item.id)
                                : [...value, item.id];

                              if (single) {
                                newValue = [item.id];
                              }

                              onChange(newValue);
                            }}
                          />
                        }
                        label={item.name}
                      />
                    ))}
                {!searchQuery &&
                  tests.map((test) => (
                    <FormControlLabel
                      key={test.id}
                      control={
                        <Checkbox
                          size="small"
                          checked={value.includes(test.id)}
                          onChange={() => {
                            let newValue = value.includes(test.id)
                              ? value.filter((id) => id !== test.id)
                              : [...value, test.id];

                            if (single) {
                              newValue = [test.id];
                            }

                            onChange(newValue);
                          }}
                        />
                      }
                      label={test.name}
                    />
                  ))}
              </Stack>
            </FormGroup>
          )}
        </Stack>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Stack direction="column" spacing={2} alignItems="stretch" justifyContent="space-between">
          <Typography variant="subtitle2">
            {single ? "Selected Test" : <span>Selected Tests ({value.length})</span>}
          </Typography>
          {value.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No dependencies selected
            </Typography>
          )}
          <FormGroup sx={{ height: "300px", overflowY: "auto" }}>
            <Stack direction="column">
              {value.map((id) => {
                const test = tests?.find((t) => t.id === id);
                return (
                  <FormControlLabel
                    key={id}
                    control={
                      <Checkbox
                        size="small"
                        checked={true}
                        onChange={() => {
                          const newValue = value.filter((v) => v !== id);
                          onChange(newValue);
                        }}
                      />
                    }
                    label={test ? test.name : `Test ${id} (not found)`}
                  />
                );
              })}
            </Stack>
          </FormGroup>
        </Stack>
      </Grid>
    </Grid>
  );
};
